import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase, getActiveSchema } from '../../../supabaseClient';

export interface HealthLog {
    measured_at: string;
    metrics: {
        bp_sys?: number;
        bp_dia?: number;
        hr?: number;
        weight?: number;
    };
}

interface HealthState {
    logs: HealthLog[];
    isSyncing: boolean;
}

interface HealthActions {
    fetchLogs: (userId: string) => Promise<void>;
    addLog: (userId: string, metrics: HealthLog['metrics']) => Promise<void>;
}

/**
 * useHealthStore (v4)
 * Unified store for Bodyweight and Vitals tracking.
 * HARDENED: Uses v3 schema and explicit metric typing.
 */
export const useHealthStore = create<HealthState & HealthActions>()(
    persist(
        (set, get) => ({
            logs: [],
            isSyncing: false,

            fetchLogs: async (userId) => {
                set({ isSyncing: true });
                try {
                    const { data, error } = await supabase
                        .schema(getActiveSchema())
                        .from('health_metrics')
                        .select('*')
                        .eq('user_id', userId)
                        .order('measured_at', { ascending: false })
                        .limit(100);
                    
                    if (error) throw error;
                    
                    // Group multiple metrics measured at the same time
                    const grouped = (data || []).reduce((acc: any, row) => {
                        const time = row.measured_at;
                        if (!acc[time]) acc[time] = { measured_at: time, metrics: {} };
                        acc[time].metrics[row.metric_type.toLowerCase()] = row.value;
                        return acc;
                    }, {});

                    set({ logs: Object.values(grouped), isSyncing: false });
                } catch (err) {
                    console.error("[HealthStore] Fetch Error:", err);
                    set({ isSyncing: false });
                }
            },

            addLog: async (userId, metrics) => {
                set({ isSyncing: true });
                const now = new Date().toISOString();
                
                const inserts = Object.entries(metrics)
                    .filter(([_, value]) => value !== undefined && value !== null)
                    .map(([key, value]) => ({
                        user_id: userId,
                        metric_type: key.toUpperCase(),
                        value: Number(value),
                        measured_at: now
                    }));

                if (inserts.length === 0) return;

                try {
                    const { error } = await supabase
                        .schema(getActiveSchema())
                        .from('health_metrics')
                        .insert(inserts);
                    
                    if (error) throw error;
                    
                    // Optimistic update
                    const newLog: HealthLog = {
                        measured_at: now,
                        metrics
                    };
                    set((state) => ({ 
                        logs: [newLog, ...state.logs],
                        isSyncing: false 
                    }));
                } catch (err) {
                    console.error("[HealthStore] Sync Error:", err);
                    set({ isSyncing: false });
                }
            }
        }),
        {
            name: 'mp-health-v3-persistence'
        }
    )
);
